/**
 * My Locations — Phase D Tenant-Admin Self-Serve
 *
 * Lets a TENANT_ADMIN user (the org owner) add another location under
 * their own tenant without involving a super-admin. Cap-enforced. When
 * a sibling location already has Gingr configured, the dialog auto-
 * discovers available Gingr partitions and inherits the api_key+app_url
 * automatically — same flow as the super-admin TenantManagement page,
 * but scoped to /api/tenants/me/* so the user can never touch any
 * other tenant.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Building2, MapPin, Plus, Loader2, ShieldCheck, CheckCircle2,
  AlertCircle, Mail, Lock,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: "", address: "", city: "", state: "", zip_code: "",
  phone: "", timezone: "America/New_York",
  gingr_location_id: "", gingr_location_name: "",
};

export default function MyLocations() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/tenants/me`);
      setTenant(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load your tenant");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenant(); }, [fetchTenant]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <div className="font-bold text-red-900">Cannot load your tenant</div>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tenant) return null;

  const locations = tenant.locations || [];
  const max = tenant.max_locations || 1;
  const atCap = !!tenant.at_cap;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5" data-testid="my-locations-page">
      {/* Tenant header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center shadow flex-shrink-0">
          <Building2 className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">My Organization</div>
          <div className="text-xl lg:text-2xl font-bold text-gray-900">{tenant.name}</div>
          <div className="text-xs text-gray-500">
            {locations.length} of {max} location{max === 1 ? "" : "s"} · {tenant.plan} plan
          </div>
        </div>
      </div>

      {/* Cap & Add control row */}
      <Card className={atCap ? "border-amber-200 bg-amber-50" : "border-indigo-200 bg-indigo-50/30"}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            {atCap ? (
              <Lock className="h-4 w-4 text-amber-700 flex-shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            )}
            <div className="text-sm">
              {atCap ? (
                <>
                  <strong className="text-amber-900">Location cap reached.</strong>{" "}
                  <span className="text-amber-800">
                    You're at {locations.length} of {max} locations. Email
                    <a href="mailto:support@fourpawiq.com" className="text-indigo-700 underline mx-1">
                      support@fourpawiq.com
                    </a>
                    to raise your cap.
                  </span>
                </>
              ) : (
                <>
                  <strong className="text-indigo-900">{max - locations.length}</strong>
                  <span className="text-indigo-800 ml-1">
                    location slot{(max - locations.length) === 1 ? "" : "s"} available on your plan.
                  </span>
                </>
              )}
            </div>
          </div>
          <AddLocationDialog
            inheritable={tenant.inheritable_gingr}
            atCap={atCap}
            onAdded={fetchTenant}
          />
        </CardContent>
      </Card>

      {/* Locations list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4 text-indigo-600" />
            Your Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {locations.length === 0 ? (
            <div className="text-sm text-gray-500 italic py-6 text-center">
              No locations yet. Click <strong>Add Location</strong> above to onboard your first resort.
            </div>
          ) : (
            <div className="space-y-2">
              {locations.map(loc => (
                <div
                  key={loc.id}
                  className="border rounded-lg p-3 flex items-center justify-between gap-3 hover:border-indigo-300 transition-colors"
                  data-testid={`my-location-${loc.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900">{loc.name}</div>
                      <div className="text-xs text-gray-500">
                        {[loc.city, loc.state].filter(Boolean).join(", ") || "No address"}
                        {" · "}
                        {loc.timezone}
                      </div>
                    </div>
                  </div>
                  {loc.has_gingr ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> Gingr OK
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      Pending Gingr
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help footer */}
      <Card className="border-gray-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Mail className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-600">
            Need to remove a location, change billing, or reassign users?
            Drop a note to <a href="mailto:support@fourpawiq.com" className="text-indigo-700 underline">support@fourpawiq.com</a> —
            we'll handle it within one business day.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ── Add Location dialog ─────────────────────────────────────────────────
function AddLocationDialog({ inheritable, atCap, onAdded }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [partitions, setPartitions] = useState(null);
  const [loadingPartitions, setLoadingPartitions] = useState(false);

  // When dialog opens with inheritable Gingr available, auto-discover partitions
  useEffect(() => {
    if (!open) return;
    setPartitions(null);
    if (inheritable?.available) {
      setLoadingPartitions(true);
      axios
        .get(`${API}/tenants/me/gingr-partitions`)
        .then(res => setPartitions(res.data))
        .catch(() => setPartitions({ success: false, error: "Discovery failed" }))
        .finally(() => setLoadingPartitions(false));
    }
  }, [open, inheritable]);

  const handlePartitionPick = (pid) => {
    const p = partitions?.partitions?.find(pp => String(pp.gingr_id) === String(pid));
    setForm(f => ({
      ...f,
      gingr_location_id: pid,
      gingr_location_name: p?.name || "",
      name: f.name.trim() === "" && p?.name ? p.name : f.name,
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Location name is required");
      return;
    }
    if (inheritable?.available && !form.gingr_location_id) {
      toast.error("Select a Gingr partition for this location");
      return;
    }
    setAdding(true);
    try {
      const body = {
        name: form.name,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        phone: form.phone || null,
        timezone: form.timezone,
      };
      if (inheritable?.available && form.gingr_location_id) {
        body.gingr_location_id = parseInt(form.gingr_location_id, 10);
        body.gingr_location_name = form.gingr_location_name || form.name;
      }
      const res = await axios.post(`${API}/tenants/me/locations`, body);
      toast.success(res.data.message);
      if (res.data.gingr_inherited) {
        toast.success("Gingr API inherited — initial sync starts within 5 min", { duration: 6000 });
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      onAdded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add location");
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        disabled={atCap}
        className="bg-indigo-600 hover:bg-indigo-700"
        data-testid="my-add-location-btn"
      >
        <Plus className="h-4 w-4 mr-1.5" /> Add Location
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-add-location-dialog">
          <DialogHeader>
            <DialogTitle>Add a New Location</DialogTitle>
            <DialogDescription>
              Onboards a new resort under your organization. If you've already connected
              Gingr, we'll inherit the API key automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Location Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. K9 Resorts of Syosset"
                data-testid="my-add-loc-name"
              />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input
                value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input
                  value={form.state}
                  onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="NY" maxLength={2}
                />
              </div>
              <div>
                <Label className="text-xs">Zip</Label>
                <Input value={form.zip_code} onChange={e => setForm(f => ({ ...f, zip_code: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Timezone</Label>
                <Select value={form.timezone} onValueChange={v => setForm(f => ({ ...f, timezone: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="America/Chicago">America/Chicago</SelectItem>
                    <SelectItem value="America/Denver">America/Denver</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {inheritable?.available && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3" data-testid="my-inherit-gingr-block">
                <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Inherit Gingr API from <strong>{inheritable.source_location_name}</strong>
                </div>
                <div className="text-xs text-emerald-800 grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-gray-500">Subdomain</span>
                    <div className="font-mono text-gray-900 font-semibold">{inheritable.app_url}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">API Key</span>
                    <div className="font-mono text-gray-900 font-semibold">{inheritable.api_key_masked}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Gingr Partition *</Label>
                    {loadingPartitions ? (
                      <div className="h-10 flex items-center text-xs text-gray-500">
                        <Loader2 className="h-3 w-3 animate-spin mr-2" /> Discovering partitions…
                      </div>
                    ) : partitions?.success && partitions.partitions.length > 0 ? (
                      <Select value={String(form.gingr_location_id)} onValueChange={handlePartitionPick}>
                        <SelectTrigger data-testid="my-add-loc-gingr-partition-select">
                          <SelectValue placeholder="Select partition…" />
                        </SelectTrigger>
                        <SelectContent>
                          {partitions.partitions.map(p => (
                            <SelectItem
                              key={p.gingr_id}
                              value={String(p.gingr_id)}
                              disabled={p.already_added}
                            >
                              #{p.gingr_id} · {p.name}{p.already_added && " (already added)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type="number" min={1}
                        value={form.gingr_location_id}
                        onChange={e => setForm(f => ({ ...f, gingr_location_id: e.target.value }))}
                        placeholder="e.g. 2"
                        data-testid="my-add-loc-gingr-partition"
                      />
                    )}
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {partitions?.success
                        ? `✓ Auto-discovered ${partitions.partitions.length} partition${partitions.partitions.length !== 1 ? "s" : ""} from Gingr`
                        : "Integer partition ID assigned by Gingr"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Gingr Location Name</Label>
                    <Input
                      value={form.gingr_location_name}
                      onChange={e => setForm(f => ({ ...f, gingr_location_name: e.target.value }))}
                      placeholder="(auto-fills from picker)"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-emerald-700 leading-snug">
                  ✓ On save, we'll auto-create a Gingr integration for this location using your existing key.
                  Initial data sync starts within 5 min.
                </div>
              </div>
            )}
            {inheritable && !inheritable.available && (
              <div className="text-xs text-gray-600 rounded border border-gray-200 bg-gray-50 p-2">
                Connect Gingr on your first location before adding more — that way new locations
                inherit the API key automatically. After creating this one, wire it up from
                Settings → Integrations.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={adding}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={adding || !form.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="my-submit-add-location"
            >
              {adding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</> : "Add Location"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
